import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  QrCode, 
  Copy, 
  Check, 
  Upload, 
  FileImage, 
  FileText,
  X,
  Loader2,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, USER_STATUS } from "@/contexts/AuthContext";
import { AppLogo } from "@/components/AppLogo";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUserStatus } = useAuth();
  const fileInputRef = useRef(null);
  
  const [copied, setCopied] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pixConfig, setPixConfig] = useState({
    pix_key: "", pix_key_type: "Email", receiver_name: "Audio Ideias", city: "", value: "29.90", description: "Assinatura Audio Ideias", qr_code_base64: ""
  });
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Load PIX config from API
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await axios.get(`${API}/pix-config`);
        setPixConfig(res.data);
      } catch {
        // defaults already set
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  // Check if user already has pending payment
  useEffect(() => {
    if (user?.status === USER_STATUS.AGUARDANDO_APROVACAO) {
      setSubmitted(true);
    }
  }, [user]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const handleCopyPix = () => {
    navigator.clipboard.writeText(pixConfig.pix_key);
    setCopied(true);
    toast.success("Chave PIX copiada!");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato inválido. Use JPG, PNG ou PDF.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 5MB.");
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedFile) {
      toast.error("Selecione um comprovante");
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      
      reader.onload = async () => {
        try {
          const base64Image = reader.result;
          
          const response = await axios.post(
            `${API}/payments/submit`,
            { proof_image: base64Image },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("voice_ideias_token")}`,
              },
            }
          );

          if (response.data.success) {
            setSubmitted(true);
            toast.success("Comprovante enviado com sucesso!");
            
            // Refresh user status
            if (refreshUserStatus) {
              refreshUserStatus();
            }
          }
        } catch (error) {
          const message = error.response?.data?.detail || "Erro ao enviar comprovante";
          toast.error(message);
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        toast.error("Erro ao processar arquivo");
        setUploading(false);
      };
    } catch (error) {
      toast.error("Erro ao enviar comprovante");
      setUploading(false);
    }
  };

  // Render submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4" data-testid="payment-submitted-page">
        <div className="mb-8 animate-fade-in-up">
          <AppLogo size="lg" />
        </div>

        <div className="w-full max-w-md animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <div className="glass-card rounded-2xl p-8 border border-emerald-500/20 text-center">
            <div className="flex justify-center mb-6 animate-scale-in" style={{ animationDelay: "0.2s" }}>
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">
              Comprovante Enviado!
            </h2>

            <p className="text-muted-foreground mb-6">
              Seu comprovante foi recebido e está aguardando análise do administrador. Você receberá acesso assim que for aprovado.
            </p>

            <div className="bg-amber-500/10 rounded-xl p-4 mb-6 border border-amber-500/20">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Aguardando aprovação</span>
              </div>
              <p className="text-xs text-muted-foreground">
                O processo de aprovação pode levar até 24 horas em dias úteis.
              </p>
            </div>

            <Link to="/">
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="back-to-home-button"
              >
                Voltar ao início
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center py-8 px-4" data-testid="payment-page">
      {/* Header */}
      <div className="mb-8 animate-fade-in-up">
        <AppLogo size="lg" />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-lg animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
        <div className="glass-card rounded-2xl p-6 md:p-8 border border-white/10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 text-center">
            Assinar Audio Ideias
          </h2>
          <p className="text-center text-muted-foreground mb-6">
            Pague via PIX e envie o comprovante
          </p>

          {/* Price */}
          <div className="bg-indigo-500/10 rounded-xl p-4 mb-6 text-center border border-indigo-500/20">
            <p className="text-sm text-muted-foreground mb-1">{pixConfig.description || "Assinatura"}</p>
            <p className="text-3xl font-bold text-white">
              R$ {(pixConfig.value || "29.90").replace(".", ",")}
              <span className="text-sm font-normal text-muted-foreground">/mês</span>
            </p>
          </div>

          {/* PIX Section */}
          <div className="space-y-4 mb-6">
            <h3 className="font-medium text-white flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-xs text-indigo-400">1</span>
              Faça o PIX
            </h3>

            {/* QR Code */}
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-xl">
                {pixConfig.qr_code_base64 ? (
                  <img src={pixConfig.qr_code_base64} alt="QR Code PIX" className="w-40 h-40 object-contain" data-testid="pix-qr-image" />
                ) : (
                  <div className="w-40 h-40 bg-gray-100 flex items-center justify-center">
                    <QrCode className="w-32 h-32 text-gray-800" />
                  </div>
                )}
              </div>
            </div>

            {/* PIX Key */}
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-2">Chave PIX ({pixConfig.pix_key_type})</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-white bg-white/5 rounded px-3 py-2 overflow-hidden text-ellipsis">
                  {pixConfig.pix_key || "Chave não configurada"}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyPix}
                  disabled={!pixConfig.pix_key}
                  className={`shrink-0 ${copied ? "text-emerald-400" : "text-white/70"}`}
                  data-testid="copy-pix-button"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {pixConfig.receiver_name && (
                <p className="text-xs text-muted-foreground mt-2">
                  Favorecido: {pixConfig.receiver_name}
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="text-sm text-muted-foreground bg-white/5 rounded-xl p-4">
              <p className="font-medium text-white/80 mb-2">Instruções:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Abra o app do seu banco</li>
                <li>Escaneie o QR Code ou copie a chave PIX</li>
                <li>Confirme o valor de R$ {(pixConfig.value || "29.90").replace(".", ",")}</li>
                <li>Faça o pagamento</li>
              </ol>
            </div>
          </div>

          {/* Upload Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-white flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-xs text-indigo-400">2</span>
              Envie o comprovante
            </h3>

            {/* File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            <>
              {selectedFile ? (
                <div
                  className="relative bg-white/5 rounded-xl p-4 border border-white/10 animate-fade-in-up"
                >
                  <button
                    onClick={handleRemoveFile}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 hover:bg-rose-500/30 transition-colors"
                    data-testid="remove-file-button"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {previewUrl ? (
                    <div className="rounded-lg overflow-hidden">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="w-full h-48 object-contain bg-black/20"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 pr-8">
                      <div className="w-12 h-12 rounded-lg bg-rose-500/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-rose-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
                    <Check className="w-4 h-4" />
                    Arquivo selecionado
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-white/10 rounded-xl p-8 hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-colors text-center"
                  data-testid="upload-button"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Clique para selecionar</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG, PNG ou PDF • Máx. 5MB
                      </p>
                    </div>
                  </div>
                </button>
              )}
            </>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitPayment}
              disabled={!selectedFile || uploading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
              data-testid="submit-payment-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Enviar comprovante
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-6 text-center animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <Link 
            to="/" 
            className="text-sm text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-2"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
        </div>

        {/* User info */}
        {user && (
          <div className="mt-4 text-center animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <p className="text-xs text-muted-foreground">
              Pagamento será vinculado a <span className="text-white/70">{user.email}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
